import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { View } from 'react-native';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyAppText from '../../../components/MyAppText';
import { translate } from '../../../services/translate';
import RadioList from '../../../components/ModernFilters/RadioList';
import BasicModal from '../../../components/BasicModal';

export type UnitRole = 'driver' | 'responsible';

interface ModalProps {
    isModalVisible: boolean;
    setModalVisible: Dispatch<SetStateAction<boolean>>;
    role: UnitRole;
    unit: AppAgentUnit;
    onConfirm: (userId: number) => Promise<boolean>;
}

interface FormValues {
    userId: number | null;
}

export default function ChangeUnitRoleModal({ isModalVisible, setModalVisible, role, unit, onConfirm }: ModalProps) {
    const { styles } = useStyles(styleSheet);

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const form = useForm<FormValues>({ defaultValues: { userId: null } });
    const userId = useWatch({ control: form.control, name: 'userId' });

    const currentUserId = useMemo(() => {
        const current = unit.UnitUsers.find(uu => role === 'driver' ? uu.UnitUser.isDriver : uu.UnitUser.isPersonInCharge);
        return current?.id ?? null;
    }, [unit, role]);

    const items = useMemo(() => {
        return unit.UnitUsers
            .filter(uu => uu.active)
            .map(uu => ({
                label: uu.warName || uu.name,
                value: uu.id,
            }));
    }, [unit]);

    useEffect(() => {
        if (isModalVisible) {
            form.reset({ userId: currentUserId });
        }
    }, [isModalVisible, currentUserId]);

    function closeModal() {
        setModalVisible(false);
    }

    async function handleSubmit() {
        if (userId === null) {
            return;
        }

        try {
            setIsLoading(true);
            const success = await onConfirm(userId);
            if (success) {
                closeModal();
            }
        } finally {
            setIsLoading(false);
        }
    }

    const title = role === 'driver' ? translate('changeDriver') : translate('changePersonInCharge');
    const subtitle = role === 'driver' ? translate('changeDriverDescription') : translate('changePersonInChargeDescription');

    return (
        <BasicModal
            isModalVisible={isModalVisible}
            setModalVisible={setModalVisible}
            title={title}
            width='small'
            mobileHeight='fit'
            primaryButton={{
                text: translate('save'),
                onClick: handleSubmit,
            }}
        >
            <View style={{ gap: 24 }}>
                <View style={styles.descriptionRow}>
                    <MyAppText style={styles.subheader}>{subtitle}</MyAppText>
                </View>
                <FormProvider {...form}>
                    <View style={styles.listContainer}>
                        <RadioList<FormValues, 'userId', number | null>
                            name='userId'
                            items={items}
                            clearValue={null}
                            hideClearValue
                            disabled={isLoading}
                        />
                    </View>
                </FormProvider>
            </View>
        </BasicModal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    subheader: {
        fontSize: 14,
        fontWeight: '400',
        flexWrap: 'wrap',
        color: theme.colors.modal.subheader,
    },
    descriptionRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    listContainer: {
        maxHeight: 360,
    },
}));
